(() => {
  if (customElements.get('bundle-builder')) return;
  const liveSelections = new Map();
  class BundleBuilder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.limit = Number(this.dataset.size);
      this.root = window.Shopify?.routes?.root || '/';
      this.storageKey = `bundle-properties-v4:${location.pathname}:${this.id}:${this.dataset.key}:${this.dataset.bundleVariant}:${this.limit}`;
      this.selected = [];
      try {
        const saved = JSON.parse(sessionStorage.getItem(this.storageKey));
        if (Array.isArray(saved)) this.selected = saved.filter(item => Number.isSafeInteger(item.id) && typeof item.title === 'string' && typeof item.variant === 'string').slice(0, this.limit);
      } catch (_) { /* Storage is optional. */ }
      if (liveSelections.has(this.storageKey)) this.selected = liveSelections.get(this.storageKey).slice(0, this.limit);
      this.cards = [...this.querySelectorAll('[data-product]')];
      // The shop map can be a JSON metafield or a legacy text field containing JSON.
      try {
        let colors = JSON.parse(this.querySelector('[data-color-map]')?.textContent || 'null');
        if (typeof colors === 'string') colors = JSON.parse(colors);
        if (Array.isArray(colors)) this.querySelectorAll('[data-color-name]').forEach(dot => {
          // Liquid already applies native swatches and section overrides first.
          if (CSS.supports('color', dot.style.getPropertyValue('--swatch').trim())) return;
          const entry = colors.find(color => String(color.name).trim().toLowerCase() === dot.dataset.colorName.trim().toLowerCase());
          if (entry && CSS.supports('color', entry.value)) dot.style.setProperty('--swatch', entry.value);
        });
      } catch (_) { /* Unmapped colors retain a visible neutral dot and text label. */ }
      this.querySelectorAll('[data-color-name]').forEach(dot => {
        if (!CSS.supports('color', dot.style.getPropertyValue('--swatch').trim())) dot.style.setProperty('--swatch', '#ddd');
      });
      this.cards.forEach(card => {
        card.productData = JSON.parse(card.querySelector('[data-product-json]').textContent);
        card.optionNamePrefix = `${this.id}-${card.dataset.product}`;
        card.relatedProducts = [card.productData, ...JSON.parse(card.querySelector('[data-related-json]')?.textContent || '[]')];
        const initial = card.productData.variants.find(v => v.id === card.productData.initial);
        card.querySelectorAll('[data-option]').forEach((group, index) => {
          group.querySelectorAll('input').forEach(input => { input.checked = input.value === initial?.options[index]; });
        });
        this.updateCard(card);
      });
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.addEventListener('change', event => {
        const card = event.target.closest('[data-product]');
        if (card && event.target.matches('[data-related-color]')) this.switchColor(card, event.target.value);
        if (card) this.updateCard(card);
      }, options);
      this.addEventListener('click', event => {
        const add = event.target.closest('[data-add]');
        const remove = event.target.closest('[data-remove]');
        if (add && !this.busy && this.selected.length < this.limit) {
          const card = add.closest('[data-product]');
          const variant = card.currentVariant;
          if (!variant?.available || card.productData.requiresSellingPlan) return;
          const lengthField = card.querySelector('[data-length-field]');
          const length = lengthField && !lengthField.hidden ? card.querySelector('[data-length]').value : '';
          const details = this.selectionDetails(card.productData, variant, length);
          this.selected.push({ id: variant.id, product: card.dataset.product, title: card.productData.title, variant: variant.title, details, image: variant.featured_image?.src || card.productData.image });
          this.status('');
          this.render();
        }
        if (remove && !this.busy) {
          const index = Number(remove.dataset.remove);
          this.selected.splice(index, 1);
          this.status('');
          this.render();
          (this.querySelectorAll('[data-remove]')[Math.min(index, this.selected.length - 1)] || this.querySelector('[data-add]:not(:disabled)'))?.focus();
        }
        if (event.target.closest('[data-submit]')) this.submit();
      }, options);
      this.resizeObserver = new ResizeObserver(entries => {
        this.style.setProperty('--bb-bar-height', `${entries[0].target.getBoundingClientRect().height}px`);
      });
      this.resizeObserver.observe(this.querySelector('.bb-bar'));
      this.render();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.initialized = false;
    }

    selectionDetails(product, variant, length = '') {
      const parts = variant.title === 'Default Title' ? [] : variant.options.map((value, index) => `${product.options[index]}: ${value}`);
      if (product.color && !product.options.some(name => /colou?r/i.test(name))) parts.unshift(`Color: ${product.color}`);
      if (length && !product.options.some(name => name.trim().toLowerCase() === 'length')) parts.push(`Length: ${length}`);
      return parts.join(' / ');
    }

    switchColor(card, productId) {
      const product = card.relatedProducts.find(item => String(item.id) === String(productId));
      if (!product) return;
      const previous = {};
      card.productData.options.forEach((name, index) => { previous[name] = card.currentVariant?.options[index]; });
      const matching = product.variants.find(variant => product.options.every((name, index) => previous[name] === variant.options[index]));
      // Preserve the selected size even when sold out, so customers can see its state.
      const initial = matching || product.variants.find(variant => variant.available) || product.variants[0];
      card.productData = product;
      card.dataset.product = String(product.id);
      const title = card.querySelector('.bb-title');
      title.textContent = product.title;
      title.href = product.url;
      const photo = card.querySelector('.bb-photo');
      photo.href = product.url;
      photo.setAttribute('aria-label', product.title);
      if (product.image) {
        let image = photo.querySelector('img');
        if (!image) { image = document.createElement('img'); image.className = 'bb-image'; photo.replaceChildren(image); }
        image.removeAttribute('srcset');
        image.src = product.image;
        image.alt = product.title;
      }
      // Ratings belong to the original product; do not display them for another color product.
      const review = card.querySelector('.bb-review');
      if (review) review.hidden = true;
      const wrapper = card.querySelector('[data-variant-options]');
      wrapper.replaceChildren();
      product.options.forEach((name, index) => {
        if (name === 'Title' && product.variants.length === 1 && initial?.title === 'Default Title') return;
        const group = document.createElement('fieldset');
        group.className = 'bb-options';
        group.dataset.option = index;
        const legend = document.createElement('legend');
        legend.className = 'bb-sr';
        legend.textContent = name;
        group.append(legend);
        [...new Set(product.variants.map(variant => variant.options[index]))].forEach(value => {
          const label = document.createElement('label');
          label.className = 'bb-choice';
          const input = document.createElement('input');
          input.type = 'radio';
          input.name = `${card.optionNamePrefix}-${index}`;
          input.value = value;
          input.checked = initial?.options[index] === value;
          const text = document.createElement('span');
          text.textContent = value;
          label.append(input, text);
          group.append(label);
        });
        wrapper.append(group);
      });
      this.updateCard(card);
      this.render();
    }

    updateCard(card) {
      const groups = [...card.querySelectorAll('[data-option]')];
      const values = groups.map(group => group.querySelector('input:checked')?.value);
      card.currentVariant = card.productData.variants.find(v => !groups.length || v.options.every((value, i) => value === values[i]));
      this.updateLength(card);
      // Availability follows preceding choices, so changing color can reveal new sizes.
      groups.forEach((group, index) => {
        group.querySelectorAll('input').forEach(input => {
          input.disabled = !card.productData.variants.some(v => v.available && v.options[index] === input.value && v.options.slice(0, index).every((value, i) => value === values[i]));
        });
      });
      const image = card.querySelector('img.bb-image');
      if (image && card.currentVariant?.featured_image?.src) {
        image.removeAttribute('srcset');
        image.src = card.currentVariant.featured_image.src;
      }
      this.updateButton(card);
    }

    updateLength(card) {
      const field = card.querySelector('[data-length-field]');
      if (!field) return;
      const product = card.productData;
      field.hidden = !product.hasTailoringLength || product.options.some(name => name.trim().toLowerCase() === 'length');
      if (field.hidden) return;
      const sizeIndex = product.options.findIndex(name => name.trim().toLowerCase() === 'size');
      const size = card.currentVariant?.options[sizeIndex]?.toUpperCase() || '';
      const key = `${product.id}:${size}`;
      if (card.lengthDefaultKey !== key) {
        card.querySelector('[data-length]').value = ({ XS: '52', S: '54', M: '56', L: '58', XL: '60' })[size] || '52';
        card.lengthDefaultKey = key;
      }
    }

    updateButton(card) {
      const button = card.querySelector('[data-add]');
      const available = card.currentVariant?.available && !card.productData.requiresSellingPlan;
      button.disabled = Boolean(this.busy || !available || this.selected.length >= this.limit);
      button.textContent = card.productData.requiresSellingPlan ? 'Subscription only' : !card.currentVariant ? 'Unavailable' : !available ? 'Sold out' : 'Add';
    }

    status(message) { this.querySelector('[data-status]').textContent = message; }

    render() {
      liveSelections.set(this.storageKey, this.selected.slice());
      try { sessionStorage.setItem(this.storageKey, JSON.stringify(this.selected)); } catch (_) { /* Storage is optional. */ }
      this.querySelector('[data-counter]').textContent = `${this.selected.length} / ${this.limit} selected`;
      this.querySelector('[data-progress]').value = this.selected.length;
      const list = this.querySelector('[data-selections]');
      list.replaceChildren();
      for (let i = 0; i < this.limit; i++) {
        const item = this.selected[i];
        const slot = document.createElement('li');
        slot.className = 'bb-slot';
        if (item) {
          slot.title = `${item.title} — ${item.variant}`;
          if (item.image) {
            const image = document.createElement('img');
            image.src = item.image;
            image.alt = slot.title;
            slot.append(image);
          } else slot.append(document.createTextNode(String(i + 1)));
          const remove = document.createElement('button');
          remove.type = 'button';
          remove.className = 'bb-remove';
          remove.dataset.remove = i;
          remove.textContent = '×';
          remove.disabled = Boolean(this.busy);
          remove.setAttribute('aria-label', `Remove ${slot.title}`);
          slot.append(remove);
        } else {
          slot.textContent = i + 1;
          slot.setAttribute('aria-label', `Empty selection ${i + 1}`);
        }
        list.append(slot);
      }
      this.cards.forEach(card => {
        this.updateButton(card);
        const count = this.selected.filter(item => item.product === card.dataset.product).length;
        card.querySelector('[data-card-count]').textContent = count ? `${count} in your bundle` : '';
      });
      const submit = this.querySelector('[data-submit]');
      submit.disabled = Boolean(this.busy || this.selected.length !== this.limit || !this.bundleAvailable());
      submit.textContent = this.busy ? 'Adding…' : this.selected.length === this.limit ? 'Add to cart' : `Select ${this.limit - this.selected.length} more`;
      if (!this.bundleAvailable()) submit.textContent = 'Bundle unavailable';
    }

    bundleAvailable() {
      return this.dataset.bundleAvailable === 'true' && Number.isSafeInteger(Number(this.dataset.bundleVariant)) && Number(this.dataset.bundleVariant) > 0;
    }

    async submit() {
      if (this.busy || this.selected.length !== this.limit || !this.bundleAvailable()) return;
      // Cart/discount redirects can navigate outside the editor's supported preview.
      // Keep selection interactive, but test checkout in the standalone storefront.
      if (window.Shopify?.designMode) {
        this.status('Your bundle is ready. Open the theme preview in a new tab to test adding it to the cart.');
        return;
      }
      this.busy = true;
      this.status('');
      this.render();
      const group = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const properties = { _bundle_id: group, _bundle_key: this.dataset.key, 'Bundle size': String(this.limit) };
      this.selected.forEach((item, index) => {
        const details = item.details || (item.variant === 'Default Title' ? '' : item.variant);
        properties[`Item ${index + 1}`] = details ? `${item.title} — ${details}` : item.title;
        properties[`_item_${index + 1}_variant_id`] = String(item.id);
      });
      const items = [{ id: Number(this.dataset.bundleVariant), quantity: 1, properties }];
      let result;
      try {
        const response = await fetch(`${this.root}cart/add.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items })
        });
        result = await response.json();
        if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this bundle. Check availability and try again.');
      } catch (error) {
        // An interrupted response can occur after Shopify has accepted the request.
        // Check the unique group before allowing a retry that could duplicate it.
        try {
          const response = await fetch(`${this.root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
          if (!response.ok) throw new Error('Cart unavailable');
          const cart = await response.json();
          if (cart.items.some(item => item.properties?._bundle_id === group)) {
            await this.finishAdd();
            return;
          }
        } catch (_) {
          this.status('We could not confirm whether the items were added. Open your cart before trying again.');
          const link = document.createElement('a');
          link.href = `${this.root}cart`;
          link.textContent = ' View cart';
          link.style.color = 'inherit';
          this.querySelector('[data-status]').append(link);
          this.querySelector('[data-submit]').textContent = 'Check your cart';
          return;
        }
        this.busy = false;
        this.render();
        this.status(error.message);
        return;
      }
      await this.finishAdd(result);
    }

    async finishAdd() {
      // A drawer refresh failure must never make an accepted cart request retryable.
      this.clearSelection();
      let warning = '';
      const code = this.dataset.code.trim();
      if (code) {
        try {
          const response = await fetch(`${this.root}cart/update.js`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ discount: code })
          });
          if (!response.ok) throw new Error('Discount update failed');
        } catch (_) { warning = 'The bundle was added, but the discount could not be applied. '; }
      }
      try {
        if (typeof window.openBundleCart === 'function') {
          await window.openBundleCart();
        } else {
          const drawer = document.querySelector('cart-drawer');
          if (!drawer?.renderContents) throw new Error('Drawer not available');
          const ids = drawer.getSectionsToRender().map(section => section.id);
          const response = await fetch(`${this.root}?sections=${encodeURIComponent(ids.join(','))}`, { cache: 'no-store' });
          if (!response.ok) throw new Error('Drawer refresh failed');
          const sections = await response.json();
          if (ids.some(id => !sections[id])) throw new Error('Drawer section missing');
          drawer.setActiveElement?.(this.querySelector('[data-submit]'));
          drawer.classList.remove('is-empty');
          drawer.renderContents({ sections });
        }
        this.status(`${warning}Bundle added to your cart.`);
      } catch (_) {
        this.status(`${warning}Your bundle was added. Open the cart from the header to review it.`);
      }
      this.busy = false;
      this.render();
    }

    clearSelection() {
      this.selected = [];
      liveSelections.delete(this.storageKey);
      try { sessionStorage.removeItem(this.storageKey); } catch (_) { /* Storage is optional. */ }
    }
  }
  customElements.define('bundle-builder', BundleBuilder);
})();
